import { useEffect } from 'react';
import {
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Row,
  Tag,
  Button,
  Space,
  Modal,
  message,
  Steps,
  Input,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { api } from '../api/client';
import { NarrativeJobBanner } from '../components/NarrativeJobBanner';
import { useImChannel } from '../hooks/useImChannel';

const PHASE_STEPS = ['DRAFT', 'ONGOING', 'CALCULATING', 'CLOSED'];

/** 评价任务状态 → 中文（避免把 PENDING/DRAFTED 等枚举码直接抛给用户）。 */
const TASK_STATUS_LABEL: Record<string, string> = {
  PENDING: '待填写',
  DRAFTED: '草稿中',
  SUBMITTED: '已提交',
  LOCKED: '已锁定',
};

/** 素质切档 JSON → 「高 30% / 中 50% / 低 20%」可读文案。 */
function formatCutoff(cutoff: unknown): string {
  if (!cutoff || typeof cutoff !== 'object') return '未设置';
  const c = cutoff as Record<string, number>;
  const pct = (v: number | undefined) =>
    typeof v === 'number' ? `${Math.round(v * 100)}%` : '-';
  return `高 ${pct(c.H)} / 中 ${pct(c.M)} / 低 ${pct(c.L)}`;
}

export function BatchDetailPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const { channel, label: imLabel } = useImChannel();

  const { data: batch } = useQuery({
    queryKey: ['batch', id],
    queryFn: () => api.get(`/admin/batches/${id}`),
  });

  const { data: progress } = useQuery({
    queryKey: ['batch-progress', id],
    queryFn: () => api.get(`/admin/eval-tasks/progress/${id}`),
    enabled: !!id,
  });

  const phaseAction = useMutation({
    mutationFn: ({ action }: { action: 'start' | 'freeze' | 'unfreeze' | 'close' | 'calc' }) =>
      api.post(`/admin/batches/${id}/${action}`),
    onSuccess: (data: any) => {
      if (data?.notificationSkipped) {
        message.info(data.notificationMessage);
      } else {
        message.success('已执行');
      }
      qc.invalidateQueries({ queryKey: ['batch', id] });
    },
  });

  const saveInfoMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.patch(`/admin/batches/${id}`, payload),
    onSuccess: () => {
      message.success('批次信息已保存');
      qc.invalidateQueries({ queryKey: ['batch', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || '保存失败'),
  });

  useEffect(() => {
    const b = batch as any;
    if (!b) return;
    const months: string[] = Array.isArray(b.performancePeriodMonths) ? b.performancePeriodMonths : [];
    form.setFieldsValue({
      displayName: b.displayName ?? '',
      name: b.name,
      startAt: b.startAt ? dayjs(b.startAt) : null,
      endAt: b.endAt ? dayjs(b.endAt) : null,
      periodStart: months.length ? dayjs(months[0], 'YYYY-MM') : null,
      periodEnd: months.length ? dayjs(months[months.length - 1], 'YYYY-MM') : null,
    });
  }, [batch, form]);

  const confirmAction = (
    action: 'start' | 'freeze' | 'unfreeze' | 'close' | 'calc',
    label: string,
  ) => {
    Modal.confirm({
      title: `确认${label}？`,
      content: '该操作将影响所有员工，写入审计日志。',
      onOk: () => phaseAction.mutate({ action }),
    });
  };

  if (!batch) return null;
  const b = batch as any;
  const editable = b.phase === 'DRAFT' || b.phase === 'ONGOING';
  const isDraft = b.phase === 'DRAFT';

  const onSaveInfo = async () => {
    try {
      const vals = await form.validateFields();
      const payload: Record<string, unknown> = {
        displayName: vals.displayName ?? '',
        endAt: vals.endAt ? (vals.endAt as dayjs.Dayjs).toISOString() : undefined,
        performancePeriodStart: vals.periodStart
          ? (vals.periodStart as dayjs.Dayjs).format('YYYY-MM')
          : undefined,
        performancePeriodEnd: vals.periodEnd
          ? (vals.periodEnd as dayjs.Dayjs).format('YYYY-MM')
          : undefined,
      };
      // 仅草稿阶段可改：技术名 / 开始时间
      if (isDraft) {
        payload.name = vals.name;
        payload.startAt = vals.startAt ? (vals.startAt as dayjs.Dayjs).toISOString() : undefined;
      }
      saveInfoMutation.mutate(payload);
    } catch {
      /* form 自校验已提示 */
    }
  };

  return (
    <div>
      <div className="tg-page-title">批次详情：{b.displayName || b.name}</div>
      <Card>
        <Steps
          current={PHASE_STEPS.indexOf(b.phase)}
          items={[
            { title: '草稿' },
            { title: '进行中' },
            { title: '核算中' },
            { title: '已关闭' },
          ]}
        />

        {editable ? (
          <Card type="inner" title="批次信息（可编辑）" style={{ marginTop: 24 }}>
            <Form form={form} layout="vertical">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item label="展示名" name="displayName">
                    <Input placeholder="业务友好名，如 2026年上半年盘点" allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="技术名"
                    name="name"
                    rules={[{ required: true, message: '技术名必填' }]}
                  >
                    <Input disabled={!isDraft} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="开始时间" name="startAt">
                    <DatePicker showTime style={{ width: '100%' }} disabled={!isDraft} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="结束时间"
                    name="endAt"
                    dependencies={['startAt']}
                    rules={[
                      { required: true, message: '结束时间必填' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const start = getFieldValue('startAt') as dayjs.Dayjs | null;
                          if (!value || !start) return Promise.resolve();
                          if ((value as dayjs.Dayjs).isBefore(start)) {
                            return Promise.reject(new Error('结束时间不能早于开始时间'));
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <DatePicker showTime style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="盘点区间起始月"
                    name="periodStart"
                    rules={[{ required: true, message: '请选择起始月' }]}
                  >
                    <DatePicker picker="month" style={{ width: '100%' }} placeholder="YYYY-MM" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="盘点区间结束月"
                    name="periodEnd"
                    rules={[{ required: true, message: '请选择结束月' }]}
                  >
                    <DatePicker picker="month" style={{ width: '100%' }} placeholder="YYYY-MM" />
                  </Form.Item>
                </Col>
              </Row>
              <Space wrap>
                <Button type="primary" loading={saveInfoMutation.isPending} onClick={onSaveInfo}>
                  保存批次信息
                </Button>
              </Space>
            </Form>
          </Card>
        ) : (
          <Descriptions style={{ marginTop: 24 }} bordered column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="ID">{b.id}</Descriptions.Item>
            <Descriptions.Item label="技术名">{b.name}</Descriptions.Item>
            <Descriptions.Item label="展示名" span={2}>
              {b.displayName ? (
                <Tag color="purple">{b.displayName}</Tag>
              ) : (
                <Typography.Text type="secondary">未设置（前端将回退使用技术名 {b.name}）</Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="开始">{dayjs(b.startAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
            <Descriptions.Item label="结束">{dayjs(b.endAt).format('YYYY-MM-DD HH:mm')}</Descriptions.Item>
            <Descriptions.Item label="盘点区间（绩效业绩）" span={2}>
              {Array.isArray(b.performancePeriodMonths) && b.performancePeriodMonths.length
                ? `${b.performancePeriodMonths[0]} ~ ${b.performancePeriodMonths[b.performancePeriodMonths.length - 1]}`
                : '未设置（默认窗口）'}
            </Descriptions.Item>
            <Descriptions.Item label="素质切档">{formatCutoff(b.qualityCutoff)}</Descriptions.Item>
          </Descriptions>
        )}

        <Descriptions style={{ marginTop: 16 }} bordered column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="高潜识别配置" span={2}>
            <Link to={`/hp?batchId=${b.id}&tab=config`}>
              配置本批次高潜四维、案例时间窗与阈值（开启批次前完成）→
            </Link>
          </Descriptions.Item>
        </Descriptions>

        <Card title="评价进度" style={{ marginTop: 16 }} type="inner">
          {progress ? (
            <Descriptions bordered column={{ xs: 1, sm: 2, md: 4 }}>
              <Descriptions.Item label="总任务">{(progress as any).total}</Descriptions.Item>
              {Object.entries((progress as any).byStatus || {}).map(([k, v]) => (
                <Descriptions.Item key={k} label={TASK_STATUS_LABEL[k] || k}>
                  {v as any}
                </Descriptions.Item>
              ))}
            </Descriptions>
          ) : (
            '加载中...'
          )}
        </Card>

        <NarrativeJobBanner batchId={b.id} />

        <Space style={{ marginTop: 24 }}>
          {b.phase === 'DRAFT' && (
            <Button
              type="primary"
              onClick={() =>
                confirmAction(
                  'start',
                  channel === 'wecom'
                    ? '开启批次（企微通知暂未开通，不会自动推送）'
                    : `开启批次（推送${imLabel}给全员）`,
                )
              }
            >
              开启批次
            </Button>
          )}
          {b.phase === 'ONGOING' && (
            <Button type="primary" danger onClick={() => confirmAction('freeze', '冻结批次（锁定所有未提交任务）')}>
              冻结进入核算
            </Button>
          )}
          {b.phase === 'CALCULATING' && (
            <>
              <Button
                type="primary"
                onClick={() =>
                  confirmAction(
                    'unfreeze',
                    '解除冻结（退回「进行中」，恢复未提交任务可打分；已提交记录不变）',
                  )
                }
              >
                解除冻结退回进行中
              </Button>
              <Button onClick={() => confirmAction('calc', '触发核算（可重复执行）')}>触发核算</Button>
              <Button onClick={() => confirmAction('close', '关闭批次（最终归档）')}>关闭批次</Button>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
